/**
 *  @class
 *  @function UniversalHero
 */

if (!customElements.get('universal-hero')) {
  class UniversalHero extends HTMLElement {
    #fgSliderCleanup = null;

    constructor() {
      super();

      this.tl = false;
      this.splittext = false;
    }

    connectedCallback() {
      this.#initFgSlider();

      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        this.prepareAnimations();
      }
    }

    disconnectedCallback() {
      if (this.#fgSliderCleanup) {
        this.#fgSliderCleanup();
        this.#fgSliderCleanup = null;
      }

      if (document.body.classList.contains('animations-true') && typeof gsap !== 'undefined') {
        if (this.tl) {
          this.tl.kill();
        }
        if (this.splittext) {
          this.splittext.revert();
        }
      }
    }

    #initFgSlider() {
      const slider = this.querySelector('.universal-hero--fg-slider');
      if (!slider) return;

      const track = slider.querySelector('.universal-hero--fg-slider-track');
      if (!track) return;

      const getSlides = () => [
        ...track.querySelectorAll('.universal-hero--fg-slider-slide:not(.hidden)'),
      ];
      if (!getSlides().length) return;

      const sliderSelector = slider.id ? `#${slider.id}` : null;
      const dotsWrap = sliderSelector
        ? this.querySelector(`.universal-hero--fg-slider-dots[data-slider="${sliderSelector}"]`)
        : null;
      const loop = slider.dataset.loop === 'true';
      const autoplay = slider.dataset.autoplay === 'true';
      const interval = parseInt(slider.dataset.autoplayInterval, 10) || 5000;
      const reducedMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
      const scrollBehavior = reducedMotion ? 'auto' : 'smooth';

      let current = 0;
      let dots = [];
      let autoplayTimer = null;
      let scrollRaf = false;

      const getActiveIndex = () => {
        const slides = getSlides();
        if (!slides.length) return 0;
        const pageWidth = track.clientWidth || 1;
        return Math.min(
          slides.length - 1,
          Math.max(0, Math.round(track.scrollLeft / pageWidth)),
        );
      };

      const syncDots = () => {
        for (const [index, dot] of dots.entries()) {
          dot.classList.toggle('is-active', index === current);
        }
      };

      const sync = () => {
        current = getActiveIndex();
        syncDots();
      };

      const buildDots = () => {
        if (!dotsWrap) return;
        const count = getSlides().length;
        if (dots.length === count) return;
        dotsWrap.innerHTML = '';
        dots = Array.from({ length: count }, (_, index) => {
          const dot = document.createElement('button');
          dot.type = 'button';
          dot.className = 'universal-hero--fg-slider-dot';
          dot.setAttribute('aria-label', `Go to slide ${index + 1}`);
          dot.addEventListener('click', () => goTo(index));
          dotsWrap.appendChild(dot);
          return dot;
        });
      };

      const goTo = (index, behavior = scrollBehavior) => {
        const slides = getSlides();
        if (!slides.length) return;

        let next = index;
        if (loop) {
          next = ((index % slides.length) + slides.length) % slides.length;
        } else {
          next = Math.max(0, Math.min(slides.length - 1, index));
        }

        const pageWidth = track.clientWidth;
        if (!pageWidth) return;

        track.scrollTo({ left: next * pageWidth, behavior });
      };

      const go = (dir) => {
        const slides = getSlides();
        if (!slides.length) return;

        if (dir > 0 && current >= slides.length - 1) {
          if (loop) goTo(0);
          return;
        }

        if (dir < 0 && current <= 0) {
          if (loop) goTo(slides.length - 1);
          return;
        }

        goTo(current + dir);
      };

      const onKeydown = (event) => {
        if (event.key === 'ArrowLeft') {
          event.preventDefault();
          go(-1);
        }
        if (event.key === 'ArrowRight') {
          event.preventDefault();
          go(1);
        }
      };

      const onScroll = () => {
        if (scrollRaf) return;
        scrollRaf = true;
        requestAnimationFrame(() => {
          sync();
          scrollRaf = false;
        });
      };

      const refresh = () => {
        buildDots();
        sync();
      };

      const stopAutoplay = () => {
        clearInterval(autoplayTimer);
        autoplayTimer = null;
      };

      const startAutoplay = () => {
        if (!autoplay || getSlides().length <= 1 || autoplayTimer) return;
        autoplayTimer = setInterval(() => go(1), interval);
      };

      track.addEventListener('keydown', onKeydown, { passive: false });
      track.addEventListener('scroll', onScroll, { passive: true });
      window.addEventListener('resize', refresh, { passive: true });
      slider.addEventListener('mouseenter', stopAutoplay);
      slider.addEventListener('mouseleave', startAutoplay);
      slider.addEventListener('focusin', stopAutoplay);
      slider.addEventListener('focusout', startAutoplay);
      track.addEventListener('pointerdown', stopAutoplay, { passive: true });

      buildDots();
      sync();
      startAutoplay();

      this.#fgSliderCleanup = () => {
        stopAutoplay();
        track.removeEventListener('keydown', onKeydown);
        track.removeEventListener('scroll', onScroll);
        window.removeEventListener('resize', refresh);
        slider.removeEventListener('mouseenter', stopAutoplay);
        slider.removeEventListener('mouseleave', startAutoplay);
        slider.removeEventListener('focusin', stopAutoplay);
        slider.removeEventListener('focusout', startAutoplay);
        track.removeEventListener('pointerdown', stopAutoplay);
      };
    }

    prepareAnimations() {
      let section = this,
        button_offset = 0,
        property = (gsap.getProperty("html", "--header-height") + gsap.getProperty("html", "--header-offset")) + 'px';

      section.tl = gsap.timeline({
        scrollTrigger: {
          trigger: section,
          start: "top center"
        }
      });

      document.fonts.ready.then(function() {
        section.splittext = new SplitText(section.querySelectorAll('h3, .rte p'), {
          type: 'lines, words',
          linesClass: 'line-child'
        });

        // Animate heading
        if (section.querySelector('h3')) {
          let h3_duration = 0.8 + ((section.querySelectorAll('h3 .line-child div').length - 1) * 0.08);
          section.tl
            .set(section.querySelector('h3'), {
              visibility: 'visible'
            }, 0)
            .from(section.querySelectorAll('h3 .line-child div'), {
              duration: h3_duration,
              yPercent: '100',
              stagger: 0.08
            }, 0);
          button_offset += h3_duration;
        }

        // Animate body text
        if (section.querySelector('.rte p')) {
          let p_duration = 0.8 + ((section.querySelectorAll('.rte p .line-child div').length - 1) * 0.02);
          section.tl
            .set(section.querySelectorAll('.rte p'), {
              visibility: 'visible'
            }, 0.2)
            .from(section.querySelectorAll('.rte p .line-child div'), {
              duration: p_duration,
              yPercent: '100',
              stagger: 0.02
            }, 0.2);
          button_offset += p_duration;
        }

        // Animate buttons
        if (section.querySelectorAll('.button').length > 0) {
          let i = 1;
          section.querySelectorAll('.button').forEach((item) => {
            section.tl.fromTo(item, {
              autoAlpha: 0
            }, {
              duration: 0.5,
              autoAlpha: 1
            }, ((button_offset * 0.4) + (i - 1) * 0.1));
            i++;
          });
        }
      });

      // Parallax effect on images
      if (section.querySelector('.thb-parallax-image')) {
        gsap.fromTo(section.querySelectorAll('.thb-parallax-image'), {
          y: '-8%'
        }, {
          y: '8%',
          ease: 'none',
          scrollTrigger: {
            trigger: section,
            scrub: 1,
            start: () => `top bottom`,
            end: () => `bottom top+=${property}`,
            onUpdate: () => {
              property = (gsap.getProperty("html", "--header-height") + gsap.getProperty("html", "--header-offset")) + 'px';
            }
          }
        });
      }
    }
  }
  customElements.define('universal-hero', UniversalHero);
}
